/* global globalThis */
import React from "react";
import PropTypes from "prop-types";
import { useStyles, useTranslations } from "sdk/custom-hooks";
import { ReactComponent as LiveIcon } from "../../assets/images/LiveImg.svg";
import moment from "moment";
import { addLiveTracking } from "sdk/actions";
import { useDispatch } from "react-redux";
import { Box } from "@material-ui/core";

const getHourAndMinute = (timeValue) => {
  if (typeof timeValue !== "string") {
    return [0, 0];
  }
  const [hour, minute] = timeValue.split(":");
  return [Number(hour) || 0, Number(minute) || 0];
};

const getStatusMeta = (isLive, isExpired, isUpcoming, t) => {
  if (isLive) {
    return { label: t("live"), color: "green" };
  }
  if (isExpired) {
    return { label: t("Expired"), color: "red" };
  }
  if (isUpcoming) {
    return { label: t("Upcoming"), color: "grey" };
  }
  return { label: "", color: "grey" };
};

export default function LiveClassCard(props) {
  const { data, sectionData } = props;
  const liveData = data?.contentData;
  const dispatch = useDispatch();
  const classes = useStyles(styles)();
  const t = useTranslations();
  const title = data?.className || "";
  const classId = data?.classId;
  const courseId = sectionData?.data?.courseId?._id;
  const quizStartDate = liveData?.startDate;
  const quizEndDate = liveData?.endDate;
  const quizStartTime = liveData?.startTime;
  const quizEndTime = liveData?.endTime;
  const currentDateTime = Date.now();

  const [startHour, startMinute] = getHourAndMinute(quizStartTime);
  const [endHour, endMinute] = getHourAndMinute(quizEndTime);

  const quizStartDateTime = quizStartDate
    ? new Date(quizStartDate).setHours(
        startHour,
        startMinute
      )
    : null;

  const quizEndDateTime = quizEndDate
    ? new Date(quizEndDate).setHours(
        endHour,
        endMinute
      )
    : null;

  const isUpcoming = quizStartDateTime !== null && currentDateTime < quizStartDateTime;
  const isLive =
    quizStartDateTime !== null &&
    quizEndDateTime !== null &&
    currentDateTime >= quizStartDateTime &&
    currentDateTime < quizEndDateTime;
  const isExpired = quizEndDateTime !== null && currentDateTime > quizEndDateTime;
  const liveClassDisable = !isLive;
  const statusMeta = getStatusMeta(isLive, isExpired, isUpcoming, t);
  const statusLabel = statusMeta.label;
  const statusColor = statusMeta.color;
  const statusBorder = `1px solid ${statusColor}`;

  const callLiveTrackingApi = () => {
    const trackingData = {
      classId,
      courseId,
    };
    dispatch(addLiveTracking(trackingData));
  };

  const handleClick = () => {
    if (liveClassDisable) {
      return;
    }
    globalThis.open?.(liveData?.url, "_blank");
    callLiveTrackingApi();
  };

  const titleText = title ? title.charAt(0).toUpperCase() + title.slice(1) : "";
  const cardClassName = liveClassDisable
    ? classes.bgDisable + " " + classes.liveclassbox
    : classes.liveclassbox;

  return (
    <Box
      className={classes.buttonDisabled}
      disabled={liveClassDisable}
      onClick={handleClick}
    >
      <Box className={cardClassName}>
        <Box className={classes.certificatediv}>
          <Box className={classes.certificatemaindiv}>
            <LiveIcon className={classes.certificateImg} />
            <Box className={classes.liveText}>
              <p className={classes.genustext}>{titleText}</p>
              <p className={classes.dateText}>
                {moment(classId?.startDate).format("DD MMM YYYY")}
              </p>
            </Box>
          </Box>
        </Box>
        <Box
          style={{ border: statusBorder }}
          className={classes.expiredQuizStatusDiv}
        >
          <p
            style={{ color: statusColor }}
            className={classes.expiredQuizText}
          >
            {statusLabel}
          </p>
        </Box>

        <Box className={classes.dashtimeDivBox}>
          <Box className={classes.dashtimeDiv}>
            <Box className={classes.timeDiv}>
              <Box className={classes.timeSpan}>{quizStartTime}</Box>
              <Box className={classes.timeSpan}>{quizEndTime}</Box>
            </Box>
            <Box className={classes.dashedLine}>
              <Box style={{ ...styles.redPoint, ...styles.leftPoint }}></Box>
              <Box style={{ ...styles.redPoint, ...styles.rightPoint }}></Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

LiveClassCard.propTypes = {
  data: PropTypes.shape({
    className: PropTypes.string,
    classId: PropTypes.shape({
      _id: PropTypes.string,
      startDate: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
    contentData: PropTypes.shape({
      startDate: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      endDate: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      startTime: PropTypes.string,
      endTime: PropTypes.string,
      url: PropTypes.string,
    }),
  }),
  sectionData: PropTypes.shape({
    data: PropTypes.shape({
      courseId: PropTypes.shape({
        _id: PropTypes.string,
      }),
    }),
  }),
};

const styles = (theme) => ({
  liveclassbox: {
    border: "1px solid #e7e7e7ee",
    display: "flex",
    margin: "15px 16px",
    padding: "12px",
    transition: "background-color 0.3s",
    cursor: "pointer",
    borderRadius: "4px",
    background: "#FFFFFF",
    "&:hover": {
      backgroundColor: "#ececec",
    },
  },
  certificatemaindiv: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: "11px",
    left: "14px",
    top: "14px",
  },
  certificateImg: {
    width: "40px",
    height: "40px",
    flexShrink: 0,
    borderRadius: "2px",
    "& path": {
      fill: "rgb(12, 52, 80)",
    },
  },
  buttonDisabled: {
    border: "0",
    width: "-webkit-fill-available",
    backgroundColor: "transparent",
    textAlign: "left",
  },
  bgDisable: {
    backgroundColor: "#ececec",
    border: "1px solid #e7e7e7ee",
    display: "flex",
    margin: "15px 16px",
    padding: "12px",
    transition: "background-color 0.3s",
    cursor: "not-allowed",
    borderRadius: "4px",
  },
  expiredQuizStatusDiv: {
    alignSelf: "center",
    fontWeight: "bold",
    marginLeft: "auto",
    border: "1px solid",
    borderRadius: "15px",
    minWidth: "50px",
  },
  expiredQuizText: {
    fontSize: "12px",
    margin: "0px",
    padding: "4px",
    textAlign: "center",
    fontWeight: "600",
    "@media (max-width: 767px)": {
      fontSize: "10px",
    },
  },
  genustext: {
    fontWeight: "500",
    margin: 0,
    color: "#000000",
    opacity: "0.6",
    fontSize: "16px",
    "@media (max-width: 959px)": {
      fontSize: "12px",
    },
    "@media (min-width: 960px) and (max-width: 1350px)": {
      fontSize: "13px",
    },
  },
  dateText: {
    margin: 0,
    fontSize: "14px",
    color: "#000000",
    opacity: "60%",
    "@media (max-width: 959px)": {
      fontSize: "10px",
    },
    "@media (min-width: 960px) and (max-width: 1350px)": {
      fontSize: "11px",
      marginTop: "7px",
    },
  },
  dashtimeDiv: {
    display: "flex",
    flexDirection: "row",
    gap: "8px",
    alignItems: "center",
  },
  dashedLine: {
    marginRight: "8px",
    borderLeft: "1px dashed #1868A0",
    height: "25px",
    position: "relative",
  },
  redPoint: {
    position: "absolute",
    width: "4px",
    height: "4px",
    background: "#1868A0",
    borderRadius: "50%",
  },
  leftPoint: {
    top: "-6px",
    left: "-2.2px",
  },
  rightPoint: {
    bottom: "-6px",
    left: "-2.8px",
  },

  timeDiv: {
    color: "#84818A",
    display: "flex",
    flexDirection: "column",
    gap: "16px",
  },
  timeSpan: {
    color: "#000000",
    opacity: "60%",
    fontSize: "12px",
    fontWeight: "300",
    "@media (max-width: 959px)": {
      fontSize: "10px",
    },
  },
  dashtimeDivBox: {
    display: "flex",
    marginLeft: "auto",
    marginRight: "2px",
  },
});
